import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, ArrowRight, Newspaper } from 'lucide-react';
import { SEO } from '../components/SEO';
import blogPostsData from '../content/blog.json';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  body: any;
  excerpt?: string;
  category?: string;
  featuredImage?: {
    url: string;
    title?: string;
  } | null;
  publishDate: string;
  featured?: boolean;
  createdAt: string;
  updatedAt: string;
}

export function BlogPostPage() {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  
  // Convert imported data to typed array
  const allPosts = blogPostsData as BlogPost[];
  
  // Find the post by slug
  const post = allPosts.find(p => p.slug === slug);
  
  // Find related posts from the same category
  const relatedPosts = post 
    ? allPosts
        .filter(p => p.category === post.category && p.slug !== slug)
        .slice(0, 2)
    : [];

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  function getImageUrl(url: string): string {
    return url.startsWith('//') ? `https:${url}` : url;
  }

  const renderOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: any, children: any) => (
        <p className="mb-6 text-gray-700 leading-relaxed">{children}</p>
      ),
      [BLOCKS.HEADING_1]: (node: any, children: any) => (
        <h1 className="text-3xl font-bold mb-6 text-gray-900">{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node: any, children: any) => (
        <h2 className="text-2xl font-semibold mb-4 text-gray-900">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node: any, children: any) => (
        <h3 className="text-xl font-semibold mb-3 text-gray-900">{children}</h3>
      ),
      [BLOCKS.UL_LIST]: (node: any, children: any) => (
        <ul className="list-disc pl-6 mb-6 space-y-2">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node: any, children: any) => (
        <ol className="list-decimal pl-6 mb-6 space-y-2">{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node: any, children: any) => (
        <li className="text-gray-700 leading-relaxed">{children}</li>
      ),
      [BLOCKS.QUOTE]: (node: any, children: any) => (
        <blockquote className="border-l-4 border-green-600 pl-4 italic my-6 text-gray-600">
          {children}
        </blockquote>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        if (!node.data.target?.fields?.file) {
          return null;
        }
        const { file, title } = node.data.target.fields;
        return (
          <img
            src={getImageUrl(file.url)}
            alt={title || ''}
            className="w-full rounded-lg my-6"
          />
        );
      },
      [INLINES.HYPERLINK]: (node: any, children: any) => (
        <a 
          href={node.data.uri} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-green-600 hover:text-green-700 underline"
        >
          {children}
        </a>
      ),
    },
  };

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center">
        <p className="text-red-600 mb-4">Post not found</p>
        <Link to="/blog" className="text-green-600 hover:text-green-700">
          Back to Blog
        </Link>
      </div>
    );
  }

  const featuredImageUrl = post.featuredImage?.url 
    ? getImageUrl(post.featuredImage.url)
    : undefined;

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt || "",
      "image": featuredImageUrl || "https://ftwlzdhkzqfuosdorgjt.supabase.co/storage/v1/object/public/playlistpartner%20public/playlistpartner%20og%20image.png",
      "datePublished": post.publishDate || post.createdAt,
      "dateModified": post.updatedAt || post.publishDate || post.createdAt,
      "author": {
        "@type": "Organization",
        "name": "Playlist Partner",
        "url": window.location.origin
      },
      "publisher": {
        "@type": "Organization",
        "name": "Playlist Partner",
        "logo": {
          "@type": "ImageObject",
          "url": "https://ftwlzdhkzqfuosdorgjt.supabase.co/storage/v1/object/public/playlistpartner%20public//playlist%20partner%20logo%20square.png",
          "width": "500",
          "height": "500"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      },
      "articleSection": post.category || "News",
      "inLanguage": "en-US"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": window.location.origin
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Blog",
          "item": `${window.location.origin}/blog`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": post.title,
          "item": window.location.href
        }
      ]
    }
  ];

  return (
    <>
      <SEO 
        title={`${post.title} | PlaylistPartner`}
        description={post.excerpt || `Read ${post.title} on the PlaylistPartner blog`}
        image={featuredImageUrl}
        type="article"
        article={{
          publishedTime: post.publishDate || post.createdAt,
          modifiedTime: post.updatedAt || post.publishDate || post.createdAt,
          author: "Playlist Partner",
          section: post.category || "News"
        }}
        structuredData={structuredData}
      />
      
      <article className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          {post.featuredImage?.url && (
            <img 
              src={getImageUrl(post.featuredImage.url)} 
              alt={post.featuredImage.title || post.title}
              className="w-full aspect-[3/2] object-cover rounded-lg mb-8"
            />
          )}

          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(post.publishDate || post.createdAt)}</span>
              </div>

              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>By <Link to="/" className="hover:text-green-600 transition-colors">Playlist Partner</Link></span>
              </div>

              {post.category && (
                <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                  <Tag className="w-3 h-3" />
                  {post.category}
                </div>
              )}
            </div>
          </header>

          <div className="prose prose-lg max-w-none mt-8 prose-ul:list-disc prose-ol:list-decimal prose-li:marker:text-gray-700">
            {post.body ? (
              documentToReactComponents(post.body, renderOptions)
            ) : (
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-600">No content available for this post.</p>
              </div>
            )}
          </div>

          {/* Filed Under Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Filed under:</span>
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full">
                <Tag className="w-3 h-3" />
                {post.category || 'News'}
              </div>
            </div>
          </div>
        </div>

        {/* Related Posts Section */}
        {relatedPosts.length > 0 && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
            <div className="flex items-center gap-3 mb-8">
              <Newspaper className="w-6 h-6 text-gray-700" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Related News & Guides
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-16">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.category?.toLowerCase() || 'news'}/${relatedPost.slug}`}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden group"
                  >
                    {relatedPost.featuredImage?.url && (
                      <div className="aspect-[3/2] overflow-hidden">
                        <img 
                          src={getImageUrl(relatedPost.featuredImage.url)} 
                          alt={relatedPost.featuredImage.title || relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      {relatedPost.excerpt && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        )}

        {/* Call-to-Action Banner */}
        <section className="bg-gradient-to-r from-green-600 to-green-700 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-2">
              Ready to Submit Your Music?
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Browse 10,000+ curated playlists
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/playlists"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-green-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Browse Playlists
              </Link>
              <Link
                to="/submit"
                className="inline-flex items-center justify-center px-6 py-3 bg-green-800 text-white font-semibold rounded-lg hover:bg-green-900 transition-colors"
              >
                Submit Track
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}